import React, { useState } from "react";
import { supabase } from "../supabaseClient";

const ExpenseForm = ({
  user,
  users,
  projects, // Receives scoped assigned projects array from parent context
  fetchTransactions
}) => {
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    to_user_id: "",
    project_id: "",
    amount: "",
    description: ""
  });

  const sendMoney = async () => {
    if (
      !form.to_user_id ||
      !form.amount ||
      !form.project_id ||
      loading
    ) {
      alert("Please select a user, project and amount.");
      return;
    }

    // Protection rule check: Validate if chosen project entry status is locked
    const selectedProjectObj = projects?.find(p => p.project_id === form.project_id);
    const projectStatus = selectedProjectObj?.status?.toLowerCase() || "";
    
    if (projectStatus === "done") {
      alert("Action Denied: You cannot initialize transactions against completed or finalized projects.");
      return;
    }

    if (projectStatus === "backlog" || projectStatus === "to do") {
      alert("Action Denied: Transactions can only be opened against Active ('In Progress' or 'Review') projects.");
      return;
    }

    setLoading(true);

    console.log("SENDING MONEY VIA RELATIONAL SCHEMAS:", {
      from_user_id: user?.id,
      to_user_id: form.to_user_id,
      project_id: form.project_id
    });

    const { data, error } = await supabase
      .from("transactions")
      .insert({
        from_user_id: user?.id,
        to_user_id: form.to_user_id,
        project_id: form.project_id,
        amount: Number(form.amount),
        note: form.description || "",
        status: "pending"
      })
      .select();

    setLoading(false);

    if (error) {
      console.error("Transaction Error:", error.message);
      alert(error.message);
      return;
    }

    console.log("TRANSACTION CREATED:", data);

    setForm({
      to_user_id: "",
      project_id: "",
      amount: "",
      description: ""
    });

    fetchTransactions();
  };

  return (
    <div
      style={{
        background: "#f3f4f6",
        padding: 20,
        borderRadius: 12,
        marginBottom: 30
      }}
    >
      <h3 style={{ marginTop: 0 }}>Send Money</h3>

      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap"
        }}
      >
        {/* USER DROPDOWN */}
        <select
          style={{
            padding: 8,
            borderRadius: 6,
            border: "1px solid #ccc",
            flex: 1
          }}
          value={form.to_user_id}
          onChange={(e) =>
            setForm({
              ...form,
              to_user_id: e.target.value
            })
          }
        >
          <option value="">Select User</option>
          {users
            .filter((u) => u.auth_id !== user?.id)
            .map((u) => (
              <option
                key={u.auth_id}
                value={u.auth_id}
              >
                {u.name}
              </option>
            ))}
        </select>

        {/* DYNAMIC RELATION-MAPPED PROJECT SELECTOR */}
        <select
          style={{
            padding: 8,
            borderRadius: 6,
            border: "1px solid #ccc",
            flex: 1
          }}
          value={form.project_id}
          onChange={(e) =>
            setForm({
              ...form,
              project_id: e.target.value
            })
          }
        >
          <option value="">Select Project</option>
          {projects
            ?.filter((project) => {
              const type = project.project_type?.toLowerCase() || "grouped";
              const status = project.status?.toLowerCase() || "";

              // Rule 1: Grouped projects are always visible here
              if (type === "grouped") return true;

              // Rule 2: Show all items so users can see completed items but grayed out
              return true; 
            })
            ?.map((project) => {
              const status = project.status?.toLowerCase() || "";
              const isLocked = status === "done" || status === "backlog" || status === "to do";
              
              // Appends helpful visual tags depending on exact operational constraints
              let statusSuffix = ` (${project.status || "Backlog"})`;
              if (status === "done") statusSuffix = " 🔒 (Completed)";
              if (status === "backlog" || status === "to do") statusSuffix = ` ⏳ (${project.status})`;

              const clientLabel = project.project_meta?.client_name 
                ? `(${project.project_meta.client_name}) ` 
                : "";
              const codeLabel = project.project_code 
                ? `[${project.project_code}] ` 
                : "";
              const typeLabel = project.project_type 
                ? ` [${project.project_type}]` 
                : " [Grouped]";

              return (
                <option
                  key={project.project_id}
                  value={project.project_id}
                  disabled={isLocked}
                >
                  {clientLabel}{codeLabel}{project.project_name}{statusSuffix}{typeLabel}
                </option>
              );
            })}
        </select>

        {/* AMOUNT */}
        <input
          style={{
            padding: 8,
            borderRadius: 6,
            border: "1px solid #ccc",
            width: "120px"
          }}
          type="number"
          placeholder="Amount"
          value={form.amount}
          onChange={(e) =>
            setForm({
              ...form,
              amount: e.target.value
            })
          }
        />

        {/* DESCRIPTION */}
        <input
          style={{
            padding: 8,
            borderRadius: 6,
            border: "1px solid #ccc",
            flex: 2
          }}
          placeholder="Description (Optional)"
          value={form.description}
          onChange={(e) =>
            setForm({
              ...form,
              description: e.target.value
            })
          }
        />

        {/* SEND BUTTON */}
        <button
          disabled={loading}
          onClick={sendMoney}
          style={{
            background: "#2563eb",
            color: "#fff",
            padding: "8px 20px",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? "Sending..." : "Send"}
        </button>
      </div>
    </div>
  );
};

export default ExpenseForm;